import { t } from '../strings';
import { formatDate, formatDateOnly, type DateFormat } from '../settings/dateFormat';

/**
 * 🔒 A date a record-page tile row names, carried as a date and never as text (decision 6701; T2973). The
 * server sends the instant or the calendar day; the screen draws it in the viewer's own date format, a
 * calendar day as it stands and an instant as the viewer's day in the viewer's zone (decision 0261).
 *
 * 🔨 A calendar day travels as midnight UTC in ISO form, the shape a date column already has, and an
 * instant as its full ISO with `instant` true (T2987). The flag tells them apart, so an instant that falls
 * exactly on UTC midnight is still drawn as the viewer's day and no text is guessed at.
 */
export interface TileRowDate {
  /** The words before the date, such as due or since; none for a bare date or a span. */
  lead?: string | null;
  /** The date, or a span's start. */
  from: string;
  /**
   * A span's end. Null is a span still running, read as Present; absent is a single date. A span whose
   * open end reads otherwise names its own words in `open`, such as no expiry.
   */
  until?: string | null;
  open?: string | null;
  /** True where `from` and `until` are instants; absent is a calendar day, a date column's midnight UTC. */
  instant?: boolean;
}

/** One part of a row's line: words as the server wrote them, or a date the screen draws (decision 6701). */
export type TileRowPart = string | TileRowDate;

/** The separator between two parts of a row's line, the one the server's own lines join on. */
export const TILE_ROW_PART_SEPARATOR = ' · ';

/** The en dash between a span's two dates, with a space either side: 10/21/2026 – Present. */
const SPAN_DASH = ' – ';

/** A date as the viewer reads it: a calendar day as it stands, an instant as the viewer's day in their zone. */
export function tileRowDay(iso: string, zone: string, format: DateFormat, instant = false): string {
  return instant ? formatDate(iso, zone, format) : formatDateOnly(iso, format);
}

/** One date part in words: its lead, the date, and a span's end or Present. */
export function tileRowDateText(part: TileRowDate, zone: string, format: DateFormat): string {
  const from = tileRowDay(part.from, zone, format, part.instant);
  const drawn = part.until === undefined
    ? from
    : `${from}${SPAN_DASH}${part.until === null ? part.open ?? t('tile.span.present') : tileRowDay(part.until, zone, format, part.instant)}`;
  return part.lead ? `${part.lead} ${drawn}` : drawn;
}

/** A row's line from its parts, each date drawn in the viewer's format, the parts joined as the server joins them. */
export function tileRowLine(parts: readonly TileRowPart[], zone: string, format: DateFormat): string {
  return parts
    .map((part) => (typeof part === 'string' ? part : tileRowDateText(part, zone, format)))
    .filter((text) => text !== '')
    .join(TILE_ROW_PART_SEPARATOR);
}
